import { useCallback, useEffect, useRef, useState } from "react";
import { TokenMintError } from "../datastore/LangGraphTokenManager";
import { ChatMessage, ChatThreadSummary, chatDataStore, formatThreadTitle } from "../datastore/ChatDataStore";
import { FmsChatThreadRowView, SENDING_PHASE, ThinkingSegment, fmsChatDisplayConverter } from "./FmsChatDisplayConverter";

export interface FmsChatStateManager {
    threadRows: FmsChatThreadRowView[];
    activeThreadId: string | null;
    messages: ChatMessage[];
    conversationTitle: string;
    statusLabel: string | null;
    isBusy: boolean;
    draft: string;
    error: string | null;
    blocked: boolean;
    pendingDeleteTitle: string | null;
    liveThinking: ThinkingSegment[] | null;
    thinkingByMsgIdx: { [idx: number]: ThinkingSegment[] };
    expandedMsgIdx: { [idx: number]: boolean };
    onDraftChange: (value: string) => void;
    onSubmit: () => void;
    onOpenThread: (threadId: string) => void;
    onStartNew: () => void;
    onRequestDelete: (threadId: string) => void;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
    onToggleThoughts: (msgIdx: number) => void;
    renamingThreadId: string | null;
    renameDraft: string;
    onStartRename: (threadId: string) => void;
    onRenameDraftChange: (value: string) => void;
    onCommitRename: () => void;
    onCancelRename: () => void;
}

export const useFmsChatStateManager = (): FmsChatStateManager => {
    const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    // Raw progress phase: a node name, the SENDING_PHASE sentinel, or null
    // (idle). The converter maps it to a label; isBusy is phase !== null.
    const [phase, setPhase] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Server-side mint rejection (403/401 after refresh): the client-side
    // group check passed but the server disagreed; the view shows the
    // invite-only state instead of a transient error line.
    const [blocked, setBlocked] = useState(false);
    // The thread awaiting delete confirmation; non-null shows the dialog.
    const [pendingDelete, setPendingDelete] = useState<ChatThreadSummary | null>(null);
    // Inline rename: the thread whose title is being edited (null = none) plus
    // the working draft. renameRef mirrors the live edit so commit (which can
    // fire from onBlur right after Esc cancels) reads the truth, and cancel can
    // null it so a trailing blur does not re-commit.
    const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState("");
    const renameRef = useRef<{ id: string; title: string } | null>(null);
    // Live thinking for the in-flight turn (expanded, streaming); null when idle.
    const [liveThinking, setLiveThinking] = useState<ThinkingSegment[] | null>(null);
    // Retained per answered turn, keyed by assistant message index. Session-only:
    // a reload re-fetches the transcript without thinking, so Thoughts start empty.
    const [thinkingByMsgIdx, setThinkingByMsgIdx] = useState<{ [idx: number]: ThinkingSegment[] }>({});
    const [expandedMsgIdx, setExpandedMsgIdx] = useState<{ [idx: number]: boolean }>({});
    // Synchronous accumulation buffer (state is async; deltas arrive fast).
    const liveThinkingRef = useRef<ThinkingSegment[]>([]);
    const openSeqRef = useRef(0);

    const appendThinking = (step: string, delta: string) => {
        const segs = liveThinkingRef.current;
        const last = segs[segs.length - 1];
        if (last && last.step === step) {
            last.text += delta;
        } else {
            segs.push({ step, text: delta });
        }
        setLiveThinking(segs.map(s => ({ ...s })));
    };

    const handleFailure = (err: unknown) => {
        liveThinkingRef.current = [];
        setLiveThinking(null);
        setPhase(null);
        if (err instanceof TokenMintError && (err.status === 403 || err.status === 401)) {
            setBlocked(true);
            return;
        }
        setError(err instanceof Error ? err.message : "Something went wrong");
    };

    const refreshThreads = useCallback(() => {
        chatDataStore.listThreads().then(setThreads).catch(() => setThreads([]));
    }, []);

    useEffect(() => {
        refreshThreads();
    }, [refreshThreads]);

    const openThread = (threadId: string) => {
        // Guard mid-run: onDone would land the in-flight run's transcript in
        // the newly selected view.
        if (phase !== null) {
            return;
        }
        const seq = ++openSeqRef.current;
        setActiveThreadId(threadId);
        setError(null);
        // Retained thinking is keyed by message index, so it must be dropped
        // when the transcript is swapped for another thread — otherwise the
        // previous thread's Thoughts would render against this thread's message
        // at the same index.
        setThinkingByMsgIdx({});
        setExpandedMsgIdx({});
        chatDataStore.getThreadMessages(threadId)
            .then(transcript => {
                if (openSeqRef.current === seq) {
                    setMessages(transcript);
                }
            })
            .catch(err => {
                if (openSeqRef.current === seq) {
                    handleFailure(err);
                }
            });
    };

    const startNewThread = () => {
        // Guard mid-run: same race as openThread.
        if (phase !== null) {
            return;
        }
        // Invalidate any in-flight openThread fetch so its transcript cannot
        // land over the fresh conversation.
        openSeqRef.current += 1;
        setActiveThreadId(null);
        setMessages([]);
        setError(null);
        // Drop retained thinking from the previous thread (keyed by message
        // index; would otherwise leak onto the fresh conversation).
        setThinkingByMsgIdx({});
        setExpandedMsgIdx({});
    };

    const performDelete = (threadId: string) => {
        chatDataStore.deleteThread(threadId)
            .then(() => {
                if (threadId === activeThreadId) {
                    startNewThread();
                }
                refreshThreads();
            })
            .catch(handleFailure);
    };

    const startRename = (threadId: string) => {
        const current = threads.find(t => t.threadId === threadId)?.title ?? "";
        renameRef.current = { id: threadId, title: current };
        setRenameDraft(current);
        setRenamingThreadId(threadId);
    };

    const changeRenameDraft = (value: string) => {
        if (renameRef.current !== null) {
            renameRef.current.title = value;
        }
        setRenameDraft(value);
    };

    const cancelRename = () => {
        renameRef.current = null;
        setRenamingThreadId(null);
    };

    const commitRename = () => {
        const edit = renameRef.current;
        renameRef.current = null;
        setRenamingThreadId(null);
        // Null when cancel already ran (e.g. Esc, then a trailing blur).
        if (edit === null) {
            return;
        }
        const title = edit.title.trim();
        // An empty title is treated as a cancel rather than clearing the name.
        if (!title) {
            return;
        }
        // Optimistic; reconcile from the server on success, and on failure too
        // so a rejected rename reverts the optimistic title to the server's
        // truth instead of leaving it stale next to the error.
        setThreads(prev => prev.map(t => (t.threadId === edit.id ? { ...t, title } : t)));
        chatDataStore.renameThread(edit.id, title)
            .then(refreshThreads)
            .catch((err) => {
                refreshThreads();
                handleFailure(err);
            });
    };

    const submit = async () => {
        const question = draft.trim();
        if (!question || phase !== null) {
            return;
        }
        // Invalidate any in-flight openThread fetch so a stale transcript
        // cannot clobber the optimistic message below.
        openSeqRef.current += 1;
        setDraft("");
        setError(null);
        setMessages(prev => [...prev, { role: "human", content: question }]);
        setPhase(SENDING_PHASE);
        liveThinkingRef.current = [];
        setLiveThinking(null);
        try {
            const threadId = activeThreadId ?? await chatDataStore.createThread();
            if (activeThreadId === null) {
                setActiveThreadId(threadId);
                // Show the new thread, titled by its first question, immediately.
                // The server derives the same title once the message lands;
                // onDone's refreshThreads reconciles this optimistic row.
                setThreads(prev => [
                    { threadId, createdAt: new Date().toISOString(), title: formatThreadTitle(question) },
                    ...prev
                ]);
            }
            await chatDataStore.sendMessage(threadId, question, {
                onNodeUpdate: (node) => setPhase(node),
                onThinking: appendThinking,
                onDone: async () => {
                    // The stream only carries progress; the transcript is
                    // re-fetched so the server stays the source of truth.
                    try {
                        const transcript = await chatDataStore.getThreadMessages(threadId);
                        setMessages(transcript);
                        const segs = liveThinkingRef.current;
                        if (segs.length > 0) {
                            const aiIdx = transcript.length - 1;
                            setThinkingByMsgIdx(prev => ({ ...prev, [aiIdx]: segs.map(s => ({ ...s })) }));
                        }
                        liveThinkingRef.current = [];
                        setLiveThinking(null);
                        // Reconcile the sidebar with the server: the optimistic
                        // new-thread row picks up its real created_at, and any
                        // custom/derived title is confirmed here.
                        refreshThreads();
                    } catch (err) {
                        handleFailure(err);
                    } finally {
                        setPhase(null);
                    }
                },
                onError: handleFailure
            });
        } catch (err) {
            // Restore the typed question so a failed send doesn't force a re-type.
            setDraft(question);
            handleFailure(err);
        }
    };

    return {
        threadRows: threads.map(t => fmsChatDisplayConverter.threadRow(t)),
        activeThreadId,
        messages,
        conversationTitle: fmsChatDisplayConverter.conversationTitle(messages),
        statusLabel: fmsChatDisplayConverter.nodeStatusLabel(phase),
        isBusy: phase !== null,
        draft,
        error,
        blocked,
        pendingDeleteTitle: pendingDelete ? (pendingDelete.title ?? "Untitled") : null,
        liveThinking,
        thinkingByMsgIdx,
        expandedMsgIdx,
        onDraftChange: setDraft,
        onSubmit: submit,
        onOpenThread: openThread,
        onStartNew: startNewThread,
        onRequestDelete: (threadId) => {
            // Guard mid-run, consistent with openThread. The view passes only
            // the id; the manager owns the model lookup (no model object leaks
            // into the view).
            if (phase !== null) {
                return;
            }
            const target = threads.find(t => t.threadId === threadId);
            if (target) {
                setPendingDelete(target);
            }
        },
        onConfirmDelete: () => {
            const target = pendingDelete;
            setPendingDelete(null);
            if (target !== null) {
                performDelete(target.threadId);
            }
        },
        onCancelDelete: () => setPendingDelete(null),
        onToggleThoughts: (i: number) => setExpandedMsgIdx(prev => ({ ...prev, [i]: !prev[i] })),
        renamingThreadId,
        renameDraft,
        onStartRename: startRename,
        onRenameDraftChange: changeRenameDraft,
        onCommitRename: commitRename,
        onCancelRename: cancelRename
    };
};
